import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserTypeLedgerQuery } from '../services/ledger/ledgerApi';
import Loading from '../components/Loading';
import LedgerSettlementModal from '../components/LedgerSettlementModal';

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const SectionTable = ({ data, onUserClick, onTransactions, onSettle, showSettle = true }) => (
  <table className="w-full text-sm border-collapse mt-2">
    <thead>
      <tr className="bg-[#0F2327] text-white">
        {['S.No', 'User Detail', 'Balance', 'Details'].map(h => (
          <th key={h} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider border-b border-gray-600 whitespace-nowrap">
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {data.length === 0 ? (
        <tr>
          <td colSpan="4" className="py-8 text-center text-slate-400 text-sm bg-slate-50/50">
            No Data
          </td>
        </tr>
      ) : (
        data.map((item, index) => (
          <tr key={index} className="bg-white border-b border-slate-100 hover:bg-slate-50 transition-colors">
            <td className="px-3 py-2.5 text-slate-500 text-xs">{item.no}</td>
            <td className="px-3 py-2.5">
              <div
                className="cursor-pointer group"
                onClick={() => onUserClick(item.userId, item.username, item.userType)}
              >
                <span className="text-teal-600 font-semibold text-sm group-hover:underline">
                  {item.username}
                </span>
                {item.fullname && (
                  <div className="text-xs text-slate-400 mt-0.5">({item.fullname})</div>
                )}
                <div className="flex items-center gap-1 text-teal-500 mt-0.5">
                  <EyeIcon />
                </div>
              </div>
            </td>
            <td className="px-3 py-2.5 font-semibold text-slate-700">{item.balance}</td>
            <td className="px-3 py-2.5">
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => onTransactions(item.userId)}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors whitespace-nowrap"
                >
                  Transactions
                </button>
                {showSettle && (
                  <button
                    onClick={() => onSettle(item)}
                    className="bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors"
                  >
                    Settle
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

function MiniLedgerPage({ userType = 'MiniAdmin', title = 'Mini Ad Ledger' }) {
  const navigate = useNavigate();
  const { data: response, isLoading, error, isFetching, refetch } = useGetUserTypeLedgerQuery({
    userType,
  });

  const [settlementModal, setSettlementModal] = useState({ isOpen: false, user: null, type: 'LENA' });

  const users = Array.isArray(response?.users) ? response.users : [];

  const lenaUsers = users.filter(u => (u.balance || '').includes('Lena'));
  const denaUsers = users.filter(u => (u.balance || '').includes('Dena'));
  const clearUsers = users.filter(u => (u.netBalance || 0) === 0);

  const lenaTotal = lenaUsers.reduce((s, u) => s + Math.abs(u.netBalance || 0), 0);
  const denaTotal = denaUsers.reduce((s, u) => s + Math.abs(u.netBalance || 0), 0);
  const clearTotal = 0;

  const toRow = (user, index) => ({
    no: index + 1,
    username: user.username,
    userId: user.userId,
    fullname: user.fullname,
    userType: user.userType,
    balance: Math.abs(user.netBalance || 0),
  });

  const lenaData = lenaUsers.map(toRow);
  const denaData = denaUsers.map(toRow);
  const clearData = clearUsers.map((u, i) => ({ ...toRow(u, i), balance: 0 }));

  const handleTransactions = (userId) => navigate(`/ledger/master-transactions/${userId}`);
  const handleSettle = (item, sectionKey) =>
    setSettlementModal({ isOpen: true, user: item, type: sectionKey === 'dena' ? 'DENA' : 'LENA' });
  const handleCloseSettlement = () => setSettlementModal({ isOpen: false, user: null, type: 'LENA' });
  const handleUserClick = (userId, username, uType) =>
    navigate(`/ledger/user-downline/${userId}/${username}/${uType}`);

  const sections = [
    {
      key: 'lena',
      label: 'Lena',
      total: lenaTotal,
      data: lenaData,
      accent: '#16a34a',
      lightBg: '#f0fdf4',
      badge: 'bg-emerald-100 text-emerald-700',
    },
    {
      key: 'dena',
      label: 'Dena',
      total: denaTotal,
      data: denaData,
      accent: '#dc2626',
      lightBg: '#fff5f5',
      badge: 'bg-red-100 text-red-700',
    },
    {
      key: 'clear',
      label: 'Clear',
      total: clearTotal,
      data: clearData,
      accent: '#2563eb',
      lightBg: '#eff6ff',
      badge: 'bg-blue-100 text-blue-700',
    },
  ];

  return (
    <div className="w-full animate-fadeIn">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* ── Page Header ────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-teal-400 rounded-full" />
            <h1 className="text-white font-semibold text-base tracking-wide">{title}</h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-all"
          >
            <BackIcon />
            Back
          </button>
        </div>

        {/* ── Content ────────────────────────────────────────────── */}
        <div className="p-4">
          {(isLoading || isFetching) ? (
            <div className="flex justify-center items-center py-20">
              <Loading />
            </div>
          ) : error ? (
            <div className="py-10 text-center text-red-500 text-sm">
              {error.message || 'Failed to load data'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sections.map(sec => (
                <div key={sec.key} className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  {/* Section header */}
                  <div
                    className="flex items-center justify-between px-4 py-3"
                    style={{ backgroundColor: sec.accent }}
                  >
                    <span className="text-white font-bold text-base tracking-wide">{sec.label}</span>
                    <span className="text-white font-bold text-base">{sec.total.toFixed(2)}</span>
                  </div>

                  {/* Section table */}
                  <div className="overflow-x-auto">
                    <SectionTable
                      data={sec.data}
                      onUserClick={handleUserClick}
                      onTransactions={handleTransactions}
                      onSettle={(item) => handleSettle(item, sec.key)}
                      showSettle={sec.key !== 'clear'}
                    />
                  </div>

                  {/* Section footer */}
                  {sec.data.length > 0 && (
                    <div
                      className="px-4 py-2 flex justify-between items-center text-xs font-semibold"
                      style={{ backgroundColor: sec.lightBg }}
                    >
                      <span className="text-slate-500">{sec.data.length} users</span>
                      <span className={`px-2 py-0.5 rounded-full ${sec.badge}`}>
                        Total: {sec.total.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      <LedgerSettlementModal
        isOpen={settlementModal.isOpen}
        onClose={handleCloseSettlement}
        user={settlementModal.user}
        type={settlementModal.type}
        onSuccess={refetch}
      />
    </div>
  );
}

export default MiniLedgerPage;
